'use client';

import { useState, useEffect } from 'react';

import Image from 'next/image';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import {
  BubbleAutopilot,
  BubbleInbound,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import { TemplatesList } from '@/components/sections/templates-list';
import Testimonials from '@/components/sections/testimonials';
import { MagicalText } from '@/components/ui/magical-text';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

const CONTENT = {
  en: {
    eyebrow: 'Helpdesks',
    heroTitleA: 'Use Open directly in',
    heroTitleB: 'helpdesk',
    heroSubtitleA: 'Full features from day one. Full control over training.',
    heroSubtitleB:
      'Transparent performance — no Fin promises, just resolutions.',
    inboundTitle: 'Plan downgrade · #4521',
    inboundPreview: 'Hey — I need to switch to the Starter plan next month.',
    autopilotTitle: 'Autopilot · ticket #4521',
    autopilotSteps: [
      'Read message in Intercom',
      'Pulled customer subscription',
      'Updated plan · Starter (May 1)',
      'Replied in Intercom thread',
      'Closed ticket · CSAT 5★',
    ],
    quoteIntroA: "Hey, I'm Mo, founder of Open.",
    quoteIntroB: "Don't take my word for it—companies like",
    automationWithOpen: 'automation with Open',
    quoteMid:
      'chose us over Fin AI. They did it because Open works better for them.',
    quoteEnd: "They're obsessed with their customers and don't compromise.",
    role: 'Founder & CEO, Open',
    moPhotoAlt: 'Mo, Founder of Open',
    vs: 'vs',
  },
  ar: {
    eyebrow: 'أنظمة الدعم',
    heroTitleA: 'استخدم Open داخل نظام دعم',
    heroTitleB: 'اللي عندك',
    heroSubtitleA: 'كل الميزات من أول يوم. والتحكّم الكامل في التدريب بيدك.',
    heroSubtitleB:
      'أداء شفّاف تراه بنفسك — بدون وعود Fin، بس حلول تقفل التذكرة.',
    inboundTitle: 'تخفيض الباقة · #4521',
    inboundPreview: 'هلا — أبي أنتقل لباقة Starter الشهر الجاي.',
    autopilotTitle: 'Autopilot · تذكرة #4521',
    autopilotSteps: [
      'قرأ الرسالة في Intercom',
      'سحب اشتراك العميل',
      'حدّث الباقة · Starter (١ مايو)',
      'ردّ في نفس محادثة Intercom',
      'أقفل التذكرة · CSAT 5★',
    ],
    quoteIntroA: 'هلا، أنا Mo، مؤسس Open.',
    quoteIntroB: 'مو لازم تصدّقني — شركات مثل',
    automationWithOpen: 'أتمتة مع Open',
    quoteMid: 'اختارونا بدل Fin AI. ليش؟ لأن Open يشتغل لهم أحسن.',
    quoteEnd: 'مهووسين بعملائهم، وما يقبلون أنصاف الحلول.',
    role: 'المؤسس والرئيس التنفيذي، Open',
    moPhotoAlt: 'Mo، مؤسس Open',
    vs: 'مقابل',
  },
} as const;

// Companies that switched from Intercom (with descriptions from logos.tsx)
const switchedCompanies = [
  {
    name: 'Viva.com',
    logo: '/logos/logo-viva.svg',
    description: 'The First Tech Bank in Europe for Businesses',
  },
  {
    name: 'More.com',
    logo: '/logos/logo-morecom.svg',
    description: 'Selling over 34 million tickets',
    automationRate: '67%',
  },
  {
    name: 'TicketSwap',
    logo: '/logos/logo-ticketswap.svg',
    description: '15 million users across 42 countries',
    automationRate: '67%',
  },
  {
    name: 'SnelEenTaxi',
    logo: '/logos/logo-sneleentaxi.svg',
    description:
      'Over half a million customers served with nationwide booking ease',
    automationRate: '65%',
  },
  {
    name: 'Tebi',
    logo: '/logos/logo-tebi.svg',
    description: 'Tebi raised a €30M Series B in June 2025',
  },
];

function SwitchedFromIntercom() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % switchedCompanies.length);
        setIsAnimating(false);
      }, 400);
    }, 2500);

    return () => clearInterval(interval);
  }, [isPaused]);

  const currentCompany = switchedCompanies[currentIndex];

  return (
    <div className="mt-20">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-col items-start gap-6 md:flex-row md:gap-8">
          {/* Mo's photo */}
          <div className="shrink-0">
            <div className="relative">
              <img
                src="/about/image.png"
                alt={c.moPhotoAlt}
                className="h-20 w-20 rounded-full object-cover shadow-xl ring-4 ring-white md:h-24 md:w-24"
              />
              <div className="absolute -right-1 -bottom-1 flex h-7 w-7 items-center justify-center rounded-full bg-black text-xs">
                👋
              </div>
            </div>
          </div>

          {/* Quote */}
          <div className="flex-1">
            <blockquote className="text-lg leading-relaxed text-neutral-700 md:text-xl">
              <span className="font-semibold text-neutral-900">
                {c.quoteIntroA}
              </span>{' '}
              {c.quoteIntroB}{' '}
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="relative mx-2 inline-flex h-6 w-24 cursor-pointer items-center justify-center align-middle md:h-7 md:w-28"
                      style={{
                        verticalAlign: 'middle',
                        transform: 'translateY(-2px)',
                      }}
                      onMouseEnter={() => setIsPaused(true)}
                      onMouseLeave={() => setIsPaused(false)}
                    >
                      <span
                        className={cn(
                          'absolute inset-0 flex items-center justify-center transition-all duration-400',
                          isAnimating
                            ? 'translate-y-2 opacity-0'
                            : 'translate-y-0 opacity-100',
                        )}
                      >
                        <img
                          src={currentCompany.logo}
                          alt={currentCompany.name}
                          className="max-h-5 max-w-full object-contain md:max-h-6"
                        />
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs px-4 py-3">
                    <p className="font-semibold text-white">
                      {currentCompany.name}
                    </p>
                    <p className="mt-1 text-sm text-neutral-300">
                      {(t.logos as Record<string, string>)[
                        currentCompany.description
                      ] || currentCompany.description}
                    </p>
                    {currentCompany.automationRate && (
                      <p className="mt-1.5 text-xs text-green-400">
                        {currentCompany.automationRate} {c.automationWithOpen}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>{' '}
              {c.quoteMid}{' '}
              <span className="font-medium text-neutral-900">{c.quoteEnd}</span>
            </blockquote>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm font-semibold text-neutral-900">
                Mo Gharbat
              </span>
              <span className="text-sm text-neutral-500">{c.role}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type ComparisonStatus = 'best' | 'worst' | 'neutral';

interface ComparisonRow {
  metric: string;
  ourAI: { value: string; status: ComparisonStatus };
  intercom: { value: string; status: ComparisonStatus };
}

export default function IntercomPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData: ComparisonRow[] = [
    {
      metric: t.helpdesk['Pricing'],
      ourAI: { value: t.helpdesk['Per outcome only'], status: 'best' },
      intercom: {
        value: t.helpdesk['Per seat + per outcome'],
        status: 'worst',
      },
    },
    {
      metric: t.helpdesk['Avg Automation Rate'],
      ourAI: { value: t.helpdesk['77% (complex B2B)'], status: 'best' },
      intercom: { value: t.helpdesk['~50-67%'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Supported Helpdesks'],
      ourAI: {
        value:
          t.helpdesk[
            'Zendesk, Salesforce, Intercom, HubSpot, Freshdesk, Twilio Flex +more'
          ],
        status: 'best',
      },
      intercom: { value: t.helpdesk['Intercom + Zendesk'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Multi-modal AI'],
      ourAI: {
        value: t.helpdesk['Voice, Vision, Text, Files'],
        status: 'best',
      },
      intercom: {
        value: t.helpdesk['Text only (rest gated)'],
        status: 'worst',
      },
    },
    {
      metric: t.helpdesk['Agentic Workflows'],
      ourAI: { value: t.helpdesk['15 triggers, 25+ actions'], status: 'best' },
      intercom: { value: t.helpdesk['Not available'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Quality Assurance'],
      ourAI: { value: t.helpdesk['Built-in auto QA'], status: 'best' },
      intercom: { value: t.helpdesk['Manual review only'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Outbound Sequencing'],
      ourAI: {
        value: t.helpdesk['WhatsApp, Phone, SMS, Email'],
        status: 'best',
      },
      intercom: { value: t.helpdesk['Limited'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Knowledge Gap Detection'],
      ourAI: { value: t.helpdesk['Auto-detect & suggest'], status: 'best' },
      intercom: { value: t.helpdesk['Not available'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Training'],
      ourAI: { value: t.helpdesk['Built-in with evals'], status: 'best' },
      intercom: { value: t.helpdesk['Limited control'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Value for Money'],
      ourAI: { value: t.helpdesk['Best ROI'], status: 'best' },
      intercom: { value: t.helpdesk['Higher total cost'], status: 'worst' },
    },
  ];

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={
          <>
            {c.heroTitleA}{' '}
            <span className="whitespace-nowrap">
              <Image
                src="/helpdicks/intercom.png"
                alt="Intercom"
                width={48}
                height={48}
                className="mr-1 inline-block h-[0.8em] w-[0.8em] rounded-[0.18em] align-[-0.1em]"
              />
              <span className="text-[#0A66C2]">Intercom</span>
            </span>{' '}
            {c.heroTitleB}
          </>
        }
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        videoSrc="/home/features/chat.mp4"
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleInbound
                source="Intercom"
                sourceLogo="/helpdicks/intercom.png"
                title={c.inboundTitle}
                preview={c.inboundPreview}
              />
            ),
          },
          {
            role: 'agent',
            widget: (
              <BubbleAutopilot
                title={c.autopilotTitle}
                steps={[...c.autopilotSteps]}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.helpdesk['Get started in minutes']}
        subtitle={
          t.helpdesk[
            'Simple setup, powerful results. Your AI agent works alongside your team in Intercom.'
          ]
        }
        learnMoreLink="#comparison"
        accentColor="blue"
        theme="light"
        benefits={[
          {
            title: t.helpdesk['Create the AI teammate in Intercom'],
            description:
              t.helpdesk[
                'Our AI will work like any other teammate in your Intercom workspace.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/intercom/create-the-ai-teammate.mp4',
            imagePosition: 'right',
          },
          {
            title: t.helpdesk['Set the routing rules'],
            description:
              t.helpdesk[
                "Route 100% or 10% of your support — it's really up to you."
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/intercom/set-the-routing-rules.mp4',
            imagePosition: 'left',
          },
          {
            title: t.helpdesk['Monitor and iterate'],
            description:
              t.helpdesk[
                'Because your new AI is just another teammate in Intercom, all of your reporting is intact. Continuously improve with feedback and training.'
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '77%',
              label: t.helpdesk['Automation Rate'],
              sublabel: t.helpdesk['Industry-leading performance'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              <MagicalText>{t.helpdesk['Our AI']}</MagicalText> {c.vs}{' '}
              {t.helpdesk['Intercom Fin AI']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  "See how our AI engine delivers better results at a better price than Intercom's Fin."
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.helpdesk['Feature']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      <MagicalText>{t.helpdesk['Our AI']}</MagicalText>
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.helpdesk['Intercom Fin AI']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/30 hover:bg-muted/30 transition-colors"
                    >
                      <TableCell className="text-foreground py-4 font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.ourAI.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.ourAI.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'ourAI' &&
                            'bg-green-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('ourAI')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.ourAI.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.ourAI.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.ourAI.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.ourAI.value}</span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.intercom.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.intercom.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'intercom' &&
                            'bg-red-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('intercom')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.intercom.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.intercom.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.intercom.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.intercom.value}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Social Proof - Companies that switched from Intercom */}
          <SwitchedFromIntercom />
        </div>
      </section>

      {/* Automate Intercom Manual Work Section */}
      <section className="bg-neutral-50 py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.helpdesk['Automate the manual tasks, not just the answers']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  'In addition to fully resolving support tickets, Open automates the manual tasks that slow down your team—routing, tagging, QA, and more.'
                ]
              }
            </p>
          </div>
          <TemplatesList
            systemFilter="Intercom"
            categoryFilter="Customer Support"
            limit={6}
            variant="slider"
          />
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.helpdesk[
            "Find answers to common questions about our Intercom AI agent. Can't find what you're looking for? Contact our support team."
          ]
        }
        items={[
          {
            question:
              t.helpdesk[
                'How long does it take to set up the platform with Intercom?'
              ],
            answer:
              t.helpdesk[
                'Our AI can be set up in under an hour. It connects directly to your Intercom instance via API and starts resolving queries on day one using your existing help center content.'
              ],
          },
          {
            question: t.helpdesk['How is our AI better than Intercom Fin?'],
            answer:
              t.helpdesk[
                'Our AI delivers superior quality from day one with no waiting period. You get all features immediately, transparent pricing with no seat fees, and a multi-model AI engine that outperforms Fin.'
              ],
          },
          {
            question: t.helpdesk['Why is our AI cheaper than Fin?'],
            answer:
              t.helpdesk[
                'Our AI costs $0.75-$0.90 per resolution with no additional seat fees or hidden costs. Intercom charges $0.99 per resolution plus additional seat fees, making our solution significantly more affordable.'
              ],
          },
          {
            question: t.helpdesk['Do I get all features on day one?'],
            answer:
              t.helpdesk[
                'Yes. Unlike Intercom which gradually rolls out features over months, our platform gives you access to all features from day one. No waiting, no phased rollouts.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Can I control what percentage of tickets the AI handles?'
              ],
            answer:
              t.helpdesk[
                'Yes. You have full control over routing rules. You can set the AI to handle 100%, 10%, or any percentage of your support tickets based on your preferences and support strategy.'
              ],
          },
          {
            question:
              t.helpdesk['How does the platform integrate with Intercom?'],
            answer:
              t.helpdesk[
                'Our AI connects via API and works as a native Intercom teammate. It syncs customer data, follows your workflows, and integrates seamlessly with your existing setup.'
              ],
          },
          {
            question: t.helpdesk['Can I test Open and Fin at the same time?'],
            answer:
              t.helpdesk[
                'Yes! We can set up Open for you in a way where you can run A/B tests between Open and Fin, and decide for yourself which performs better. Try it risk-free for 1 month.'
              ],
          },
          {
            question: t.helpdesk['Can I try Open risk-free?'],
            answer:
              t.helpdesk[
                "Absolutely. You can try Open risk-free for a full month with free implementation. Our team will set everything up for you at no cost, and you only pay if you're satisfied with the results."
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
